import conversionByRate from './conversion-by-rate.js';
import deprecationWarning, { maybeReportDifferentTypesDeprecated } from './deprecation-warning.js';
import isUnitType from './is-unit-type.js';
import BigIntWrapper from '@exodus/bigint';
import { createNumber, stringToNumberFraction } from './number-unit-utils.js';
import { CurrencyIdSymbol } from './constants.js';
import _isNumberUnit from './is-number-unit.js';
const TEN = BigIntWrapper.wrap(10);
export default class NumberUnit {
    static create(num, unit) {
        return new NumberUnit(num, unit);
    }
    static get [CurrencyIdSymbol]() {
        return 'NumberUnit';
    }
    static isInstance(instance) {
        return this[Symbol.hasInstance](instance);
    }
    static [Symbol.hasInstance](instance) {
        if (!instance?.constructor ||
            Object.hasOwn(instance, 'constructor')) {
            return false;
        }
        if (Function.prototype[Symbol.hasInstance].call(NumberUnit, instance)) {
            return true;
        }
        const considerInstance = NumberUnit.name === instance.constructor.name;
        return (considerInstance &&
            instance.constructor[CurrencyIdSymbol] === NumberUnit[CurrencyIdSymbol]);
    }
    static isNumberUnit = _isNumberUnit;
    _number;
    unit;
    unitType;
    baseUnit;
    defaultUnit;
    unitName;
    _numberStringMap;
    constructor(numberLike, unit) {
        this.unit = unit;
        this.unitType = unit.unitType;
        this.baseUnit = unit.unitType.baseUnit;
        this.defaultUnit = unit.unitType.defaultUnit;
        this.unitName = this.unit.unitName;
        this._number = createNumber(numberLike, unit.power);
        Object.defineProperty(this, '_numberStringMap', {
            enumerable: false,
            value: new Map(),
            writable: true,
        });
    }
    abs() {
        return new NumberUnit(this._number.abs(), this.unit);
    }
    add(num) {
        num = this._coerceToNumberUnit(num);
        maybeReportDifferentTypesDeprecated(this, num, 'add');
        if (num.isZero)
            return this;
        if (this.isZero)
            return new NumberUnit(num._number, this.unit);
        return new NumberUnit(this._number.add(num._number), this.unit);
    }
    clampLowerZero() {
        const zero = new NumberUnit(0, this.unit);
        if (this.gte(zero))
            return this;
        return zero;
    }
    clone() {
        return new NumberUnit(this._number, this.unit);
    }
    equals(num) {
        maybeReportDifferentTypesDeprecated(this, num, 'equals');
        return this._number.eq(num._number);
    }
    gt(num) {
        maybeReportDifferentTypesDeprecated(this, num, 'gt');
        return this._number.gt(num._number);
    }
    gte(num) {
        maybeReportDifferentTypesDeprecated(this, num, 'gte');
        return this._number.gte(num._number);
    }
    lt(num) {
        maybeReportDifferentTypesDeprecated(this, num, 'lt');
        return this._number.lt(num._number);
    }
    lte(num) {
        maybeReportDifferentTypesDeprecated(this, num, 'lte');
        return this._number.lte(num._number);
    }
    mul(num) {
        if (NumberUnit.isNumberUnit(num)) {
            throw new TypeError(`NumberUnit.mul does not support NumberUnit as argument.`);
        }
        if (BigIntWrapper.isUnderlyingInstance(num)) {
            num = BigIntWrapper.wrap(num);
        }
        if (num instanceof BigIntWrapper) {
            return new NumberUnit(this._number.mul(num), this.unit);
        }
        const { number, fraction, fractionLength } = stringToNumberFraction(num, 0);
        const result = number !== '0' && number !== '-0'
            ? this._number.mul(BigIntWrapper.wrap(number))
            : BigIntWrapper.wrap(0);
        if (fraction !== '0' && fraction !== '-0') {
            const fractionBN = BigIntWrapper.wrap(fraction)
                .mutateMul(this._number)
                .div(TEN.pow(BigIntWrapper.wrap(fractionLength)));
            result.mutateAdd(fractionBN);
        }
        return new NumberUnit(result, this.unit);
    }
    div(num) {
        if (NumberUnit.isNumberUnit(num)) {
            throw new TypeError(`NumberUnit.div does not support NumberUnit as argument.`);
        }
        if (BigIntWrapper.isUnderlyingInstance(num)) {
            return new NumberUnit(this._number.div(num), this.unit);
        }
        const { number, fraction, fractionLength } = stringToNumberFraction(num, 0);
        const fractionPow = TEN.pow(BigIntWrapper.wrap(fractionLength));
        const divisor = BigIntWrapper.wrap(number)
            .mutateMul(fractionPow)
            .mutateAdd(BigIntWrapper.wrap(fraction));
        if (divisor.isZero())
            throw new Error('Invalid division by 0');
        const bn = this._number.mul(fractionPow).div(divisor);
        return new NumberUnit(bn, this.unit);
    }
    floor() {
        if (this.unit.power === 0) {
            return this;
        }
        const num = this._toNumberString();
        const [whole] = num.split('.');
        return new NumberUnit(whole, this.unit);
    }
    round() {
        if (this.unit.power === 0) {
            return this;
        }
        const num = this._toNumberString();
        const [whole, fraction] = num.split('.');
        let newNumber = new NumberUnit(whole, this.unit);
        if (fraction && Math.round(Number.parseFloat(`0.${fraction}`)) > 0) {
            newNumber = newNumber.add(new NumberUnit(1, this.unit));
        }
        return newNumber;
    }
    ceil() {
        if (this.unit.power === 0) {
            return this;
        }
        const num = this._toNumberString();
        const [whole, fraction] = num.split('.');
        let newNumber = new NumberUnit(whole, this.unit);
        if (fraction && Math.ceil(Number.parseFloat(`0.${fraction}`)) > 0) {
            newNumber = newNumber.add(new NumberUnit(1, this.unit));
        }
        return newNumber;
    }
    toFixed(x, rm) {
        const factor = BigIntWrapper.wrap(10).pow(BigIntWrapper.wrap(x));
        let num = new NumberUnit(this._number.mul(factor), this.unit);
        switch (rm) {
            case 'floor':
                num = num.floor();
                break;
            case 'ceil':
                num = num.ceil();
                break;
            default:
                num = num.round();
        }
        num = new NumberUnit(num._number.div(factor), this.unit);
        return num;
    }
    negate() {
        return new NumberUnit(this._number.negate(), this.unit);
    }
    sub(num) {
        num = this._coerceToNumberUnit(num);
        maybeReportDifferentTypesDeprecated(this, num, 'sub');
        if (num.isZero)
            return this;
        if (this.isZero)
            return new NumberUnit(num._number.negate(), this.unit);
        return new NumberUnit(this._number.sub(num._number), this.unit);
    }
    cast(unitType) {
        if (!isUnitType(unitType))
            throw new Error('unitType needs to be a UnitType instance');
        if (this.unitType.equals(unitType))
            return this.toBase();
        const unit = unitType.baseUnit;
        let number = this._number;
        const thisPower = this.unitType.defaultUnit.power;
        const otherPower = unitType.defaultUnit.power;
        if (number.isZero() || thisPower === otherPower)
            return new NumberUnit(number, unit);
        const multiplier = TEN.pow(BigIntWrapper.wrap(Math.abs(otherPower - thisPower), 10));
        if (thisPower < otherPower) {
            number = number.mul(multiplier);
        }
        else if (thisPower > otherPower) {
            number = number.div(multiplier);
        }
        return new NumberUnit(number, unit);
    }
    toBase() {
        if (this.unit.power === 0) {
            return this;
        }
        return new NumberUnit(this._number, this.baseUnit);
    }
    toDefault() {
        if (this.defaultUnit === this.unit)
            return this;
        return this.to(this.defaultUnit);
    }
    to(unit, conversionUnit) {
        if (unit === this.unit)
            return this;
        if (typeof unit === 'string') {
            unit = this.unitType.units[unit];
        }
        if (this.unit.unitType.equals(unit.unitType)) {
            return new NumberUnit(this._number, unit);
        }
        if (!conversionUnit)
            throw new Error('Incompatible unit types. Must specify a conversion.');
        if (typeof conversionUnit === 'function') {
            return conversionUnit(this);
        }
        if (!this.unitType.equals(conversionUnit.from.unitType)) {
            throw new Error('Conversion unit from is of different type.');
        }
        const rate = Number.parseFloat(conversionUnit.toValue) /
            Number.parseFloat(conversionUnit.fromValue);
        const convert = conversionByRate(conversionUnit.from.unitType, conversionUnit.to.unitType, rate, { unit1: conversionUnit.fromUnit, unit2: conversionUnit.toUnit });
        return convert(this);
    }
    toJSON() {
        return {
            value: this._toNumberString(),
            unit: this.unitName,
            unitType: this.unitType.toString(),
            type: 'NumberUnit',
        };
    }
    toNumber(unit) {
        return Number.parseFloat(this._toNumberString(unit));
    }
    toDefaultNumber() {
        return this.toNumber(this.defaultUnit);
    }
    toBaseNumber() {
        return this.toNumber(this.baseUnit);
    }
    toBaseBufferLE(length) {
        return this._number.toBaseBufferLE(length);
    }
    toBaseBufferBE(length) {
        return this._number.toBaseBufferBE(length);
    }
    toNumberString(unit) {
        deprecationWarning('DEPRECATION WARNING: number-unit.toNumberString usage will be removed soon. Use number-unit.toString({unit: false}) instead');
        return this._toNumberString(unit);
    }
    _toNumberString(unit = this.unit) {
        const noInstanceWarning = 'WARNING: number-unit was cloned improperly and is no longer a NumberUnit instance';
        const unitString = unit.toString();
        if (this._numberStringMap && this._numberStringMap.get(unitString)) {
            return this._numberStringMap.get(unitString);
        }
        if (!this._numberStringMap) {
            deprecationWarning(noInstanceWarning);
        }
        const numberUnit = unit === this.unit ? this : this.to(unit);
        let number = numberUnit._number.toString(10);
        if (numberUnit.unit.power === 0) {
            if (this._numberStringMap)
                this._numberStringMap.set(unitString, number);
            else
                deprecationWarning(noInstanceWarning);
            return number;
        }
        const isNeg = number[0] === '-';
        if (isNeg) {
            number = number.slice(1);
        }
        if (number.length < numberUnit.unit.power + 1) {
            number = number.padStart(numberUnit.unit.power + 1, '0');
        }
        number = number.slice(0, -numberUnit.unit.power) + '.' + number.slice(-numberUnit.unit.power);
        const pp = number.indexOf('.');
        if (pp !== -1) {
            let zp = number.length - 1;
            while (number[zp] === '0' && zp > pp)
                zp -= 1;
            if (zp !== number.length - 1) {
                if (number[zp] === '.')
                    zp--;
                number = number.slice(0, zp + 1);
            }
        }
        if (isNeg) {
            number = '-' + number;
        }
        if (this._numberStringMap)
            this._numberStringMap.set(unitString, number);
        else
            deprecationWarning(noInstanceWarning);
        return number;
    }
    toString({ unit = true, format, unitInstance = this.unit, } = {}) {
        if (unitInstance) {
            unitInstance = this.unitType.units[unitInstance.toString()];
        }
        if (format) {
            return format(this._toNumberString(unitInstance), unitInstance);
        }
        return this._toNumberString(unitInstance) + (unit ? ' ' + unitInstance.unitName : '');
    }
    toDefaultString({ unit = false } = {}) {
        return this.toString({ unitInstance: this.defaultUnit, unit });
    }
    toBaseString({ unit = false } = {}) {
        return this.toString({ unitInstance: this.baseUnit, unit });
    }
    valueOf() {
        return this.toDefaultNumber();
    }
    get value() {
        return this.toString();
    }
    get isNegative() {
        return this._number.isNegative();
    }
    get isPositive() {
        return this._number.isPositive();
    }
    get isZero() {
        return this._number.isZero();
    }
    _coerceToNumberUnit(number) {
        if (NumberUnit.isNumberUnit(number))
            return number;
        deprecationWarning('DEPRECATION WARNING: number-unit.add/sub usage with int or string param will be removed soon');
        return new NumberUnit(number, this.unit);
    }
}
